import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import intl from 'react-intl-universal';
import { TabPanels, TabPanel, Tabs } from '@reach/tabs';
import { Tab, TabList, tailwindOverride } from '@getsynapse/design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Task } from 'utils/customTypes';
import { TaskFormFields } from 'utils/types/task';
import { TaskTemplate } from 'utils/types/taskTemplate';
import { taskModalFields } from 'utils/constants/task';
import { useTaskBundleDraft } from '../../providers/TaskBundleProvider/TaskBundleProvider';
import { useDependencyManagement } from '../../hooks/useDependencyManagement';
import useModal from 'Hooks/useModal';
import DependenciesSection from '../DependenciesSection/DependenciesSection';
import TaskForm from 'Organisms/TaskModal/TaskForm';
import UnsavedChangesModal from 'Organisms/UnsavedChangesModal/UnsavedChangesModal';
import Timeline from '../DetailsSection/Timeline';

type TaskTemplateModalMode = 'create' | 'edit';

interface TabbedTaskTemplateModalProps {
  mode: TaskTemplateModalMode;
  isOpen: boolean;
  onClose: () => void;
  taskTemplateData?: TaskTemplate;
}

const buildTaskData = (template?: TaskTemplate): Task => ({
  id: template?.id ?? '',
  name: template?.name ?? '',
  description: template?.description,
  type: template?.type,
  estimate_hours: template?.estimated_hours ?? '',
  createdAt: '',
});

const TabbedTaskTemplateModal = ({
  mode,
  isOpen,
  onClose,
  taskTemplateData,
}: TabbedTaskTemplateModalProps) => {
  const { actions, selectors } = useTaskBundleDraft();
  const { Modal, modalProps } = useModal();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [canCreateTask, setCanCreateTask] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const { relativeDates = false } = useFlags();

  const initialTaskFields = useMemo<TaskFormFields>(() => {
    const initialData = buildTaskData(taskTemplateData);
    return {
      ...taskModalFields,
      name: initialData.name,
      description: initialData.description,
      type: initialData.type,
      estimate_hours: initialData.estimate_hours ?? '',
    };
  }, [taskTemplateData]);

  const [taskFields, setTaskFields] =
    useState<TaskFormFields>(initialTaskFields);

  const [startDay, setStartDay] = useState<string>(
    taskTemplateData?.start_day ?? ''
  );
  const [duration, setDuration] = useState<string>(
    taskTemplateData?.duration ?? ''
  );

  const isEditing = mode === 'edit';
  const isCreating = mode === 'create';

  const bundleId = selectors.getBundleId();

  const {
    showDependencies,
    dependenciesMap,
    dependenciesChanged,
    handleDependencyChange,
    getMappedDependencies,
    getAvailableTasks,
    resetDependencies,
    dependenciesCount,
  } = useDependencyManagement({
    bundleId,
    templateId: taskTemplateData?.id,
    isModalOpen: isOpen,
  });

  const taskData = useMemo(
    () => buildTaskData(taskTemplateData),
    [taskTemplateData]
  );

  const initialTimeline = useMemo(
    () => ({
      startDay: taskTemplateData?.start_day ?? '',
      duration: taskTemplateData?.duration ?? '',
    }),
    [taskTemplateData]
  );

  const normalizeEstimateHours = (value: any) => {
    const num = parseFloat(value);
    return isNaN(num) || num === 0 ? '' : num.toString();
  };

  const didDetailsChange = useMemo(() => {
    return (
      taskFields.name !== initialTaskFields.name ||
      taskFields.description !== initialTaskFields.description ||
      normalizeEstimateHours(taskFields.estimate_hours) !==
        normalizeEstimateHours(initialTaskFields.estimate_hours) ||
      taskFields.type !== initialTaskFields.type ||
      startDay !== initialTimeline.startDay ||
      duration !== initialTimeline.duration
    );
  }, [taskFields, initialTaskFields, startDay, duration, initialTimeline]);

  const isTimelinePartial =
    (startDay !== '' && duration === '') ||
    (startDay === '' && duration !== '');

  const canConfirm =
    !isTimelinePartial &&
    ((isCreating && canCreateTask) ||
      (isEditing && (didDetailsChange || dependenciesChanged)));

  useEffect(() => {
    if (isOpen) {
      setTaskFields(initialTaskFields);
      setStartDay(initialTimeline.startDay);
      setDuration(initialTimeline.duration);
    }
  }, [isOpen, initialTaskFields, initialTimeline]);

  const closeModal = useCallback(() => {
    onClose();
    resetDependencies();
    setActiveTab(0);
    setCanCreateTask(false);
    setTaskFields(taskModalFields);
    setStartDay('');
    setDuration('');
  }, [onClose, resetDependencies]);

  const handleClose = useCallback(() => {
    // If there are unsaved changes, ask for confirmation before closing
    if (didDetailsChange || dependenciesChanged) {
      setShowUnsavedChangesModal(true);
      return;
    }
    closeModal();
  }, [didDetailsChange, dependenciesChanged, closeModal]);

  const handleConfirm = useCallback(() => {
    const { predecessors, successors } = getMappedDependencies();
    if (isCreating) {
      const newId = uuidv4();
      actions.addTemplate({
        id: newId,
        name: taskFields.name,
        description: taskFields.description,
        estimated_hours: taskFields.estimate_hours,
        type: taskFields.type,
        start_day: startDay || undefined,
        duration: duration || undefined,
        dependencies: {
          predecessors: predecessors ?? [],
          successors: successors ?? [],
        },
      });
    } else if (isEditing && taskTemplateData) {
      if (didDetailsChange) {
        // Details changed (possibly alongside dep changes): use updateTemplate
        // which marks the template as modified so it is included in the next
        // bulk create/update call.
        actions.updateTemplate(taskTemplateData.id, {
          name: taskFields.name,
          description: taskFields.description,
          estimated_hours: taskFields.estimate_hours,
          type: taskFields.type,
          start_day: startDay || undefined,
          duration: duration || undefined,
          dependencies: {
            predecessors: predecessors ?? [],
            successors: successors ?? [],
          },
        });
      } else if (dependenciesChanged) {
        // Dep-only edit: update dependencies without marking the template as
        // needing a full resave, avoiding spurious bulkCreateOrUpdateTemplates
        // calls and subtle field-normalisation overwrites.
        actions.replaceTemplateDependencies(taskTemplateData.id, {
          predecessors: predecessors ?? [],
          successors: successors ?? [],
        });
      }
    }
    closeModal();
  }, [
    taskFields,
    startDay,
    duration,
    taskTemplateData,
    actions,
    getMappedDependencies,
    isCreating,
    isEditing,
    didDetailsChange,
    dependenciesChanged,
    closeModal,
  ]);

  const title = intl.get('TASK_MODAL.TITLE', {
    action: isEditing ? intl.get('UPDATE') : intl.get('ADD'),
  });

  return (
    <Modal
      {...modalProps}
      isOpen={isOpen}
      onClose={handleClose}
      aria-label={title}
      title={title}
      size='large'
      className='h-full'
      closeModal={handleClose}
      overlayClassName='z-50'
      childrenClassName='max-h-112'
      actionButtons={[
        {
          children: title,
          onClick: handleConfirm,
          disabled: !canConfirm,
          'data-testid': 'confirm-task-template',
          variant: 'primary',
        },
        {
          children: intl.get('CANCEL'),
          onClick: closeModal,
          variant: 'tertiary',
          'data-testid': 'cancel-task-template',
        },
      ]}
    >
      <Tabs
        index={activeTab}
        className='flex flex-col min-h-full max-w-full'
        onChange={setActiveTab}
      >
        <TabList
          type='subHeader'
          className='border-b border-primary-lighter-two mb-4'
        >
          <Tab index={0} type='subHeader'>
            {intl.get('SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.DETAIL')}
          </Tab>
          <Tab index={1} type='subHeader' className='flex items-center gap-x-2'>
            {intl.get(
              'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.SECTION_TITLE'
            )}
            {dependenciesCount > 0 && (
              <span
                className={tailwindOverride(
                  'w-5 h-5 rounded-full',
                  'bg-secondary-lightest text-neutral',
                  'flex items-center justify-center',
                  'text-xs font-semibold'
                )}
              >
                {dependenciesCount}
              </span>
            )}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <div className='flex flex-col gap-y-6'>
              <TaskForm
                canCreateTask={canCreateTask}
                isTaskTemplate
                isCentralizedTask={false}
                taskData={taskData}
                projectId=''
                setTaskFields={setTaskFields}
                setCanCreateTask={setCanCreateTask}
              />
              {relativeDates && (
                <Timeline
                  startDay={startDay}
                  duration={duration}
                  onStartDayChange={setStartDay}
                  onDurationChange={setDuration}
                  isPartial={isTimelinePartial}
                />
              )}
            </div>
          </TabPanel>
          <TabPanel>
            {showDependencies && (
              <DependenciesSection
                dependencies={dependenciesMap}
                onChange={handleDependencyChange}
                availableTasks={getAvailableTasks()}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        setIsOpen={setShowUnsavedChangesModal}
        onConfirm={() => {
          setShowUnsavedChangesModal(false);
          closeModal();
        }}
        overlayClassName='z-50'
      />
    </Modal>
  );
};

export default TabbedTaskTemplateModal;
